import {CookieSecurityOptions} from 'types/Cookie'

import {vclidCookieConfig} from '../../analytics/utils/vclidCookie'
import {getAllCookies, setCookie} from '../utils/cookies'
import {cookieSecuritySettings} from '../utils/cookies/cookieSecuritySettings'
import {Attribution} from './types'

interface AttributionCookieConfig {
  name: string
  options: CookieSecurityOptions
}

/**
 * `vtm_src` and `label` are stored separately from the SAPI label: the SAPI
 * label machinery keeps running next to them and neither writes the other's
 * values.
 */
export const vtmSrcCookieConfig: AttributionCookieConfig = {
  name: 'vtm_src',
  options: cookieSecuritySettings(true)
}

export const vtmLabelCookieConfig: AttributionCookieConfig = {
  name: 'vtm_label',
  options: cookieSecuritySettings(true)
}

/**
 * Reads the store from a cookie map, for code that is handed its cookies
 * rather than reading `document.cookie`, such as the server render.
 * @param cookies - The request's cookies, by name
 */
export const readStoredAttribution = (
  cookies: Partial<Record<string, string>>
): Attribution => ({
  vtmSrc: cookies[vtmSrcCookieConfig.name] || '',
  label: cookies[vtmLabelCookieConfig.name] || '',
  vclid: cookies[vclidCookieConfig.name] || ''
})

export const readAttributionStore = (): Attribution =>
  readStoredAttribution(getAllCookies())

/**
 * The only writer of the attribution store. All three values are overwritten
 * together, so the expiry counts from the landing that set them rather than
 * sliding on every landing.
 */
export const writeAttributionStore = (
  {vtmSrc, label, vclid}: Attribution,
  expirationHours: number
) => {
  const expires = new Date(Date.now() + expirationHours * 60 * 60 * 1000)

  const write = ({name, options}: AttributionCookieConfig, value: string) => {
    const {secure, sameSite} = options
    setCookie(name, value, {expires, secure, sameSite})
  }

  write(vclidCookieConfig, vclid)
  write(vtmSrcCookieConfig, vtmSrc)
  write(vtmLabelCookieConfig, label)
}
